import {Component, ElementRef, inject, input, model, output} from '@angular/core';
import {clampPanelWidth} from './panel-width';

/**
 * Which edge of the resized panel the handle sits on.
 *
 * `start` — the handle is on the panel's **leading** (left) edge, so dragging *left* makes the panel wider
 * (the Offene-Tische list rail, which hangs off the right of the screen).
 * `end` — the handle is on the panel's **trailing** (right) edge, so dragging *right* makes it wider
 * (the Operator navigation rail, which hangs off the left).
 */
export type ResizeHandleEdge = 'start' | 'end';

/** How many pixels one arrow key moves the edge. */
export const RESIZE_STEP_PX = 16;

/** How many pixels one Page Up / Page Down moves the edge — a coarse jump, four arrow steps. */
export const RESIZE_PAGE_PX = RESIZE_STEP_PX * 4;

/**
 * A draggable splitter between a resizable panel and its neighbour.
 *
 * **It is a real control, not a decoration**, and is built as one: an ARIA *window splitter*
 * (`role="separator"` with `tabindex`, `aria-orientation`, `aria-valuenow/min/max`), so it is reachable and
 * operable from the keyboard — <kbd>←</kbd>/<kbd>→</kbd> nudge by {@link RESIZE_STEP_PX}, <kbd>Page Up</kbd>/
 * <kbd>Page Down</kbd> by {@link RESIZE_PAGE_PX}, <kbd>Home</kbd>/<kbd>End</kbd> jump to the bounds — and not
 * only from a mouse. Pointer events (rather than mouse events) are used throughout, so touch and pen drags work
 * with the same code path; `setPointerCapture` keeps the drag alive when the pointer outruns the 6 px hit area,
 * and `touch-action: none` stops a touch drag from scrolling the page instead.
 *
 * **The component owns no policy.** Bounds, the persisted value and *whether the handle exists at all* belong to
 * the host: resizing is a desktop affordance, and at phone widths both of this app's resizable panels stop being
 * side-by-side layouts (the Offene-Tische screen stacks, the Operator rail becomes an overlay drawer), so the
 * hosts render the handle only while their wide layout is active. Keeping that decision out of here means the
 * handle can never be a stray tab stop on a phone.
 *
 * Two outputs on purpose: {@link width} updates continuously so the panel tracks the pointer, while
 * {@link committed} fires once the interaction settles — that is the one a host should persist from, so a drag
 * is one storage write rather than sixty.
 *
 * @example
 * ```html
 * <app-resize-handle edge="start" label="Breite der Tischliste ändern"
 *                    [(width)]="listWidth" [min]="MIN" [max]="max()"
 *                    (committed)="persistWidth($event)"/>
 * ```
 */
@Component({
  selector: 'app-resize-handle',
  standalone: true,
  host: {
    'role': 'separator',
    'aria-orientation': 'vertical',
    'tabindex': '0',
    '[attr.aria-label]': 'label()',
    '[attr.aria-valuenow]': 'width()',
    '[attr.aria-valuemin]': 'min()',
    '[attr.aria-valuemax]': 'max()',
    '[attr.aria-valuetext]': 'width() + " Pixel"',
    '[class.rh-dragging]': 'dragging',
    '[class.rh-divider]': 'showDivider()',
    '(pointerdown)': 'onPointerDown($event)',
    '(pointermove)': 'onPointerMove($event)',
    '(pointerup)': 'onPointerEnd($event)',
    '(pointercancel)': 'onPointerEnd($event)',
    '(keydown)': 'onKeyDown($event)'
  },
  styles: [`
    /* Inline component styles are parsed as plain CSS, not SCSS, so block comments are the only legal form
       in here — a double-slash line comment would break the production style build while staying invisible
       to both tsc and the unit-test run. */
    :host {
      flex: 0 0 auto;
      display: block;
      position: relative;
      width: 6px;
      align-self: stretch;
      box-sizing: border-box;
      background: transparent;
      cursor: col-resize;
      /* Without this a touch drag scrolls the page instead of moving the splitter. */
      touch-action: none;
      transition: background .12s;
    }

    /* The hit area is 6 px wide; the divider it draws is 1 px, centred in it, so the grab target can be
       comfortable without the seam getting heavier. Hosts whose panel already draws its own edge switch it off
       with [showDivider]="false" rather than ending up with two parallel lines. */
    :host(.rh-divider)::before {
      content: '';
      position: absolute;
      top: 0;
      bottom: 0;
      left: 50%;
      width: 1px;
      transform: translateX(-50%);
      background: #e2e0dc;
      transition: background .12s;
    }

    :host(:hover),
    :host(.rh-dragging) {
      background: rgba(197, 14, 108, .18);
    }

    :host(.rh-divider:hover)::before,
    :host(.rh-divider.rh-dragging)::before {
      background: #c50e6c;
    }

    :host(:focus-visible) {
      outline: 2px solid #c50e6c;
      outline-offset: -1px;
    }

    /* Coarse pointers (touch) cannot hit a 6 px target; widen it without changing the visible line. */
    @media (pointer: coarse) {
      :host {
        width: 14px;
      }
    }

    @media (prefers-reduced-motion: reduce) {
      :host {
        transition: none;
      }
    }
  `],
  template: ''
})
export class ResizeHandleComponent {
  private readonly element = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;

  /** The current panel width in pixels. Two-way: the handle writes it live while the pointer moves. */
  readonly width = model.required<number>();

  /** Narrowest the panel may become — the host derives it from where its content stops wrapping. */
  readonly min = input.required<number>();

  /** Widest the panel may become, so it can never swallow the screen. */
  readonly max = input.required<number>();

  /** Which side of the panel the handle is on — decides whether dragging left or right grows it. */
  readonly edge = input<ResizeHandleEdge>('start');

  /** The control's accessible name. Required: an unnamed splitter is unusable with a screen reader. */
  readonly label = input.required<string>();

  /**
   * Whether the handle draws the seam between the two panels. Switch it off where the resized panel already has
   * its own border on that side, so the two do not stack into a double line.
   */
  readonly showDivider = input(true);

  /**
   * The settled width, emitted once per interaction (pointer release, or immediately on a keyboard nudge).
   * Hosts persist from here rather than from `widthChange`, so a drag costs one storage write.
   */
  readonly committed = output<number>();

  /** Whether a pointer drag is in flight — drives the active styling and gates `pointermove`. */
  protected dragging = false;

  /** Pointer x and panel width at the moment the drag started; every move is measured against these. */
  private startX = 0;
  private startWidth = 0;

  protected onPointerDown(event: PointerEvent): void {
    // Ignore secondary buttons: a right-click should open the context menu, not start a silent resize.
    if (event.button !== 0) return;
    this.dragging = true;
    this.startX = event.clientX;
    this.startWidth = this.width();
    this.capture(event.pointerId, true);
    // Suppress the browser's text selection / image drag for the duration of the gesture.
    event.preventDefault();
  }

  protected onPointerMove(event: PointerEvent): void {
    if (!this.dragging) return;
    const travel = event.clientX - this.startX;
    // A handle on the panel's leading edge grows the panel when the pointer moves *left*, hence the sign flip.
    this.width.set(clampPanelWidth(
      this.startWidth + (this.edge() === 'start' ? -travel : travel), this.min(), this.max()));
    event.preventDefault();
  }

  protected onPointerEnd(event: PointerEvent): void {
    if (!this.dragging) return;
    this.dragging = false;
    this.capture(event.pointerId, false);
    this.committed.emit(this.width());
  }

  /**
   * Takes or releases pointer capture, which is what keeps the drag alive once the pointer leaves the 6 px
   * strip — without it the very first move outside the handle would end the gesture.
   *
   * Both calls throw `NotFoundError` when the id names no *active* pointer, which happens for a synthesised
   * event (every component test dispatches one) and after the browser has already ended the gesture itself.
   * Capture is an optimisation for a drag that is tracked in this component's own state either way, so a
   * failure to take it is not worth an exception.
   */
  private capture(pointerId: number, take: boolean): void {
    try {
      if (take) this.element.setPointerCapture?.(pointerId);
      else this.element.releasePointerCapture?.(pointerId);
    } catch {
      // No active pointer with that id — see above.
    }
  }

  /**
   * Keyboard resizing — the reason this is a `separator` with a `tabindex` rather than a styled `<div>`.
   *
   * Arrow semantics are **spatial, not panel-relative**: <kbd>→</kbd> always moves the splitter right, whichever
   * side of it the panel is on, because that is what someone watching the screen expects. <kbd>Home</kbd>/
   * <kbd>End</kbd> are panel-relative (narrowest / widest), matching how a slider's extremes read.
   */
  protected onKeyDown(event: KeyboardEvent): void {
    const grow = this.edge() === 'start' ? -1 : 1;
    let next: number | null = null;

    switch (event.key) {
      case 'ArrowLeft':
        next = this.width() - RESIZE_STEP_PX * grow;
        break;
      case 'ArrowRight':
        next = this.width() + RESIZE_STEP_PX * grow;
        break;
      case 'PageUp':
        next = this.width() + RESIZE_PAGE_PX;
        break;
      case 'PageDown':
        next = this.width() - RESIZE_PAGE_PX;
        break;
      case 'Home':
        next = this.min();
        break;
      case 'End':
        next = this.max();
        break;
      default:
        return;
    }

    const clamped = clampPanelWidth(next, this.min(), this.max());
    event.preventDefault();
    if (clamped === this.width()) return;
    this.width.set(clamped);
    this.committed.emit(clamped);
  }
}
